/**
 * Auth Service — Cognito authentication via AWS Amplify.
 *
 * Provides login, logout, token access, and a shared getAuthHeaders()
 * function that all API modules should use for authenticated requests.
 *
 * Amplify automatically handles:
 *   - Token storage (in-memory + browser storage)
 *   - Silent token refresh via Refresh Token
 *   - Token expiration detection
 */

import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

/**
 * Sign in directly with Cognito via Amplify.
 * No backend Lambda involved in authentication.
 *
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{success: boolean, challenge?: object, error?: string}>}
 */
export async function login(username, password) {
  try {
    // Clear any stale Amplify auth state from a previous session or
    // incomplete sign-in flow to avoid "already a signed in user" errors
    await signOut();
  } catch {
    // Ignore — signOut throws if nobody is signed in
  }
  try {
    const result = await signIn({ username, password });
    if (result.isSignedIn) {
      return { success: true };
    }
    // Handle challenges (NEW_PASSWORD_REQUIRED, MFA, etc.)
    return { success: false, challenge: result.nextStep };
  } catch (err) {
    return { success: false, error: err.message || 'Authentication failed' };
  }
}

/**
 * Sign out — clears all Amplify-managed tokens.
 */
export async function logout() {
  try {
    await signOut();
  } catch (err) {
    console.error('[auth] Sign out error:', err);
  }
}

// ---------------------------------------------------------------------------
// Token Access
// ---------------------------------------------------------------------------

/**
 * Get the current ID token for API requests.
 * Amplify automatically refreshes expired tokens using the Refresh Token.
 *
 * @returns {Promise<string|null>} The ID token string, or null if not authenticated
 */
export async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Check if user is currently authenticated with valid tokens.
 *
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Shared Auth Headers
// ---------------------------------------------------------------------------

/**
 * Returns standard auth headers for all PalCare API requests.
 * Replaces every `'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN`
 * with a per-user JWT Bearer token.
 *
 * Usage:
 *   const headers = await getAuthHeaders();
 *   fetch(url, { method: 'POST', headers, body });
 *
 * @returns {Promise<object>} Headers object with Content-Type and Authorization
 */
export async function getAuthHeaders() {
  const idToken = await getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
}
